import { SlideStatement } from "../../../SlideStatement";
import { SupLink } from "../../../SlideContext";

// The Raise (split from the old slide 19, TPF 2026-09-04): terms and the
// month-24 destination only. Where the money goes is slide 20, Use of Funds.
// Every number here is an output of the Pre-Seed Use of Funds Model sheet
// (base scenario, snapshot 2026-09-05: €1.25M raise, €180K founder pay,
// PM/QE/DevOps cut, Stripe fees + affiliate share included).
const TERMS: Array<{ value: string; label: string; sub: string }> = [
  { value: "€1.25M", label: "Raising", sub: "the only round we plan to need" },
  { value: "SAFE", label: "Instrument", sub: "post-money SAFE, standard docs" },
  { value: "12–15%", label: "Dilution", sub: "€8.3–10.4M post-money" },
  { value: "24 mo", label: "Horizon", sub: "funding day → cash-flow positive" },
];

export default function TheRaise() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-8 md:[&_header]:mb-0">
      <SlideStatement eyebrow="The Raise">
        <div>
          <h1 className="text-3xl font-semibold leading-[1.05] tracking-tight md:text-7xl">
            €1.25M to reach €3.7M ARR, cash-flow positive in 24 months.
          </h1>
          {/* Restates what the ask buys — above the rule with the claim. */}
          <p className="mt-4 text-xl font-medium tracking-tight text-slate-600 md:mt-5 md:text-2xl">
            Revenue covers the whole 24-month plan; the raise carries the
            first-year trough and stays as cushion.
            <SupLink to="use-of-funds">19</SupLink>
          </p>
        </div>
      </SlideStatement>

      {/* Below the rule — the terms, then the destination they buy. */}
      <div className="flex flex-col gap-5 md:min-h-0 md:flex-1 md:gap-3.5 md:pt-6">

        {/* Terms */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
        {TERMS.map((t) => (
          <div
            key={t.label}
            className="rounded-xl bg-white p-4 ring-1 ring-slate-200 md:p-5"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-900 md:text-sm">
              {t.label}
            </p>
            <p className="mt-1 text-2xl font-bold tracking-tight text-blue-700 md:text-4xl">
              {t.value}
            </p>
            <p className="mt-1 text-xs text-slate-500 md:text-sm">{t.sub}</p>
          </div>
        ))}
      </div>

        {/* Month 24 — the destination */}
        <div className="md:min-h-0 md:flex-1">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700 md:tracking-[0.2em]">
          Month 24 — what success looks like
        </p>
        <div className="mt-3 overflow-hidden rounded-2xl bg-blue-50 ring-1 ring-blue-200">
          <p className="px-6 pt-5 text-lg font-semibold leading-snug text-blue-900 md:px-8 md:pt-4 md:text-xl">
            Cash-flow positive at{" "}
            <span className="font-bold">~€3.7M ARR, growing 2.8× year-over-year</span>{" "}
            at ~45% operating margin — which makes a Series A{" "}
            <span className="font-bold">optional</span>: raise from strength
            for acceleration, or don't raise at all.
          </p>
          <div className="mt-4 grid grid-cols-2 gap-3 px-6 pb-5 md:mt-4 md:grid-cols-4 md:gap-4 md:px-8 md:pb-4">
            <Metric value="€3.7M" label="ARR run-rate" sub="base plan · €309K MRR" />
            <Metric value="m13" label="revenue covers burn" sub="~€1.4M in bank at m24" />
            <Metric value="2,163 + 26" label="accounts at m24" sub="self-serve + enterprise" />
            <Metric value="4 FT" label="team at m24" sub="2 founders + PMM + MOps, + PT & BD" />
          </div>
          <p className="border-t border-blue-200/70 bg-white/60 px-6 py-3 text-sm leading-snug text-slate-700 md:px-8 md:py-3 md:text-base">
            <span className="font-semibold text-slate-900">
              Sized above the downside — a lean team, GTM-heavy, for 24 months:
            </span>{" "}
            the plan needs ~€950K; the extra €300K holds it to a blended CAC of
            ~€310 (2.5× plan) or an ARPU of ~€60 (half of plan). A third under
            plan still ends m24 at ~€2.4M ARR with ~€550K in bank — no bridge
            round.
          </p>
        </div>
      </div>
      </div>
    </div>
  );
}

function Metric({
  value,
  label,
  sub,
}: {
  value: string;
  label: string;
  sub: string;
}) {
  return (
    <div className="rounded-lg bg-white p-3 ring-1 ring-blue-200/70 md:p-3.5">
      <p className="text-xl font-bold tracking-tight text-slate-900 md:text-3xl">
        {value}
      </p>
      <p className="mt-0.5 text-xs font-medium text-slate-900 md:text-sm">
        {label}
      </p>
      <p className="text-xs text-slate-500 md:text-sm">{sub}</p>
    </div>
  );
}
